/* eslint-disable react-hooks/rules-of-hooks */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { triggerDownload, createBulkAttachmentsDownload, createReportDownload } from 'src/utils/downloadUtils';
import { t } from 'i18next';
import { $api } from './api';
import { components, paths } from '../generated/v1';
import { hideSpinnerToast, showSpinnerToast } from '../utils/spinnerToaster';

export type ReportType = components['schemas']['ReportTypeSelection'];
export type Report = components['schemas']['AdvancedReport'];

export const useReportsQuery = (params?: paths['/advanced_reports/report/type']['get']['parameters']['query']) => {
  return $api.useQuery('get', '/advanced_reports/report/type', {
    params: {
      query: params,
    },
  });
};

export const useUpdateReportType = () => {
  const queryClient = useQueryClient();

  return $api.useMutation('put', '/advanced_reports/report/type/{id}', {
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['get', '/advanced_reports/report/type'] });
    },
  });
};

export const useReportTypeQuery = (
  code: string,
  params?: paths['/advanced_reports/report/types/{code}']['get']['parameters']['query'],
) => {
  return $api.useQuery('get', '/advanced_reports/report/types/{code}', {
    params: {
      path: {
        code,
      },
      query: params,
    },
  });
};

export const useReportTypeTableQuery = (type: string) => {
  return (params: {
    page: number;
    size: number;
    sort_by: string | undefined;
    sort_order: 'asc' | 'desc' | undefined;
    domain: string | undefined;
    tabIndex?: number;
  }) => {
    return useReportTypeQuery(type, {
      page: params.page,
      size: params.size,
      sort_by: params.sort_by,
      sort_order: params.sort_order,
      domain: params.domain,
    });
  };
};

export const useAttachmentsBulk = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ model, ids }: { model: string; ids: number[] }) => {
      const config = createBulkAttachmentsDownload(model, ids);
      await triggerDownload(config);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['get', '/advanced_reports/report/types/{code}'],
      });
    },
  });
};

export const useCreateReportByType = (reportType: string) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data?: any) => {
      const config = createReportDownload(reportType, data || {});
      await triggerDownload(config);
    },

    onMutate: () => {
      showSpinnerToast(t(`reports.generating`));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['get', '/advanced_reports/report/type'] });
      queryClient.invalidateQueries({ queryKey: ['get', '/advanced_reports/report/types/{code}'] });
    },

    onSettled: () => {
      hideSpinnerToast();
    },

    onError: error => {
      console.error('Report creation failed:', error);
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending,
  };
};
